import { byId, clear, el, setBusy, setText } from "../dom";
import { formatRelative } from "../format";
import type { PQRSItem, PQRSStatus } from "../types";
import type { LoadStatus } from "./operation";
import { emptyState, errorState, skeletonRows } from "./states";

export interface PqrsState {
  status: LoadStatus;
  items: readonly PQRSItem[];
  total: number;
  error: string | null;
}

export interface PqrsHandlers {
  onReload(status: string): void;
  onStatus(id: string, status: PQRSStatus): void;
}

export interface PqrsController {
  setState(state: PqrsState): void;
  filter(): string;
}

const KIND_LABEL: Record<string, string> = {
  peticion: "Petición",
  queja: "Queja",
  reclamo: "Reclamo",
  sugerencia: "Sugerencia",
};

const STATUS_LABEL: Record<PQRSStatus, string> = {
  abierta: "Abierta",
  en_proceso: "En proceso",
  cerrada: "Cerrada",
};

export function initPqrs(handlers: PqrsHandlers): PqrsController {
  const listElement = byId<HTMLUListElement>("pqrs-list");
  const summary = byId<HTMLParagraphElement>("pqrs-summary");
  const filterSelect = byId<HTMLSelectElement>("pqrs-filter");
  const reloadButton = byId<HTMLButtonElement>("pqrs-reload");

  let items: readonly PQRSItem[] = [];
  let status: LoadStatus = "idle";
  let error: string | null = null;
  let total = 0;

  function statusChipClass(item: PQRSItem): string {
    if (item.status === "abierta") return "chip chip--new";
    return "chip";
  }

  function render(): void {
    setBusy(reloadButton, status === "loading");
    clear(listElement);

    if (status === "idle" || status === "loading") {
      listElement.append(skeletonRows(2));
      setText(summary, "Cargando solicitudes…");
      return;
    }
    if (status === "error") {
      listElement.append(
        errorState({
          title: "No pudimos cargar las PQRS",
          message: error ?? "Revisa la conexión e intenta de nuevo.",
          onRetry: () => handlers.onReload(filterSelect.value),
        }),
      );
      setText(summary, "Solicitudes sin cargar");
      return;
    }
    if (items.length === 0) {
      listElement.append(
        emptyState({
          iconName: "i-bell",
          title: "Sin PQRS por ahora",
          message: "Las solicitudes radicadas en el sitio público aparecerán aquí.",
        }),
      );
      setText(summary, "Solicitudes del sitio público");
      return;
    }

    const now = Date.now();
    for (const item of items) {
      const card = el("li", { className: "alert" });
      const head = el("div", { className: "alert__head" });
      head.append(
        el("span", {
          className: "alert__title",
          text: `${KIND_LABEL[item.kind] ?? item.kind} · ${item.name}`,
          title: item.id,
        }),
        el("span", { className: "alert__time", text: formatRelative(item.created_at, now) }),
      );

      const chips = el("div", { className: "alert__chips" });
      chips.append(el("span", { className: statusChipClass(item), text: STATUS_LABEL[item.status] }));

      card.append(
        head,
        el("p", {
          className: "alert__route",
          text: item.address.trim() === "" ? item.contact : `${item.contact} · ${item.address}`,
        }),
        el("p", { className: "alert__route", text: item.message }),
        chips,
      );

      if (item.status !== "cerrada") {
        const actions = el("div", { className: "device__actions" });
        if (item.status === "abierta") {
          const progress = el("button", { className: "btn btn--ghost btn--sm", text: "Marcar en proceso" });
          progress.type = "button";
          progress.addEventListener("click", () => handlers.onStatus(item.id, "en_proceso"));
          actions.append(progress);
        }
        const close = el("button", { className: "btn btn--ghost btn--sm", text: "Cerrar" });
        close.type = "button";
        close.addEventListener("click", () => handlers.onStatus(item.id, "cerrada"));
        actions.append(close);
        card.append(actions);
      }

      listElement.append(card);
    }

    setText(summary, `${total} ${total === 1 ? "solicitud" : "solicitudes"} · ${filterLabel(filterSelect.value)}`);
  }

  function filterLabel(value: string): string {
    switch (value) {
      case "abierta":
        return "abiertas";
      case "en_proceso":
        return "en proceso";
      case "cerrada":
        return "cerradas";
      default:
        return "todas";
    }
  }

  reloadButton.addEventListener("click", () => handlers.onReload(filterSelect.value));
  filterSelect.addEventListener("change", () => handlers.onReload(filterSelect.value));

  return {
    setState(next) {
      status = next.status;
      items = next.items;
      total = next.total;
      error = next.error;
      render();
    },

    filter() {
      return filterSelect.value;
    },
  };
}
